import { Box } from '@mui/material';
import { WidgetSelector, WidgetOption } from './WidgetSelector';
import { ProbabilityDistributionWidget } from './ProbabilityDistributionWidget';
import { BackpressureDistributionWidget } from './BackpressureDistributionWidget';
import { PositionsDistributionWidget } from './PositionsDistributionWidget';

type SetDefinition = Models.SetsPlayground.SetDefinition;
type SetDistribution = Models.SetsPlayground.SetDistribution;
type DistType = SetDistribution['type'];

export const DISTRIBUTION_HELP_TEXT = 'Guaranteed Share (aka Backpressure) places this set in the share of '
    + 'positions you set, spaced evenly through the gallery. A share of 0.2 means about 1 in every 5 tiles. '
    + 'Random Chance (aka Probability) gives each position that chance of coming from this set, so the real '
    + 'share varies from page to page and is not evenly spaced. Explicit Positions pins the set to specific tiles.';

interface Props {
    index: number;
    set: SetDefinition;
    onChangeDistribution: (index: number, dist: SetDistribution) => void;
    disabled?: boolean;
}

export const DistributionWidgetContainer = ({
    index, set, onChangeDistribution, disabled,
}: Props): JSX.Element => {
    const { distribution } = set;
    const selected = distribution.type;

    const distributionWidgets: WidgetOption<SetDistribution>[] = [
        ProbabilityDistributionWidget as WidgetOption<SetDistribution>,
        BackpressureDistributionWidget as WidgetOption<SetDistribution>,
        PositionsDistributionWidget as WidgetOption<SetDistribution>,
    ];

    const handleWidgetChange = (value: string): void => {
        const nextType = value as DistType;

        if (nextType !== distribution.type) {
            const widget = distributionWidgets.find((w) => w.value === nextType);

            if (widget?.getDefaultValue) {
                const defaultValue = widget.getDefaultValue();

                onChangeDistribution(index, defaultValue);
            }
        }
    };

    const handleDistributionChange = (newDistribution: SetDistribution): void => {
        onChangeDistribution(index, newDistribution);
    };

    return (
        <Box>
            <WidgetSelector
                disabled={disabled}
                label="Distribution Type"
                selected={selected}
                widgets={distributionWidgets}
                widgetValue={distribution}
                onWidgetChange={handleWidgetChange}
                onWidgetValueChange={handleDistributionChange}
            />
        </Box>
    );
};
